import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import * as yup from "yup";
import FileUploadArea, { FileUploadAreaRef } from "./FileUploadArea";
import { apiService } from "../../../../api/api";
import { bannerDashboardApi } from "../../../../api/bannerDashboardApi";
import {
  BannerSwapRequest,
  BannerSwapQueueRequest,
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapResolution,
  ChannelSettings,
  ChannelSettingsWithId,
} from "../../../../utils/@types";
import { useSelector } from "react-redux";
import { BannerSwap } from "../../../../utils/@types";
import { ROUTES } from "../../../../config/routes";
import { channelsMissingDefaultBanner, resolveBannerState } from "../bannerStateUtils";
import MissingDefaultBannerDialog from "../MissingDefaultBannerDialog";
import {
  getInvalidDefaultBannerRows,
  toMissingDefaultChannels,
} from "../bannerSwapQueueErrorUtils";
import SwapOverlapResolutionDialog from "../SwapOverlapResolutionDialog";
import {
  buildClientOverlapCheckResult,
  overlapSummaryText,
} from "../bannerSwapOverlapUtils";
import { resolveCurrentBannerDisplayUrl } from "../bannerImageUtils";
import { formatSwapDateCell } from "../../../BannerDashboard/bannerDashboardUtils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { storage } from "../../../../Features/config/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import "./BannerSwapForm.css";
import "../../../BannerDashboard/bannerSwapDialogs.css";
import { trackToolAction } from "../../../../utils/analytics";

// Wrapper component that properly implements BannerTemplateDefault as a React component
const BannerTemplateDefaultWrapper: React.FC<{
  channelInfo: ChannelSettingsWithId;
}> = ({ channelInfo }) => {
  const [url, setUrl] = useState<string>("");

  useEffect(() => {
    const fetchImageUrl = async () => {
      try {
        const storageRef = ref(storage, channelInfo.DefaultBannerURL);
        const downloadUrl = await getDownloadURL(storageRef);
        if (downloadUrl) {
          setUrl(downloadUrl);
        }
      } catch (error) {
        setUrl("");
      }
    };

    if (channelInfo.ChannelId) {
      fetchImageUrl();
    }
  }, [channelInfo.DefaultBannerURL]);

  return (
    <div className="w-full h-full">
      <img
        src={channelInfo.DefaultBannerURL}
        alt={"Unable to access"}
        className="max-w-full w-[15vw]"
        style={{ maxWidth: "100%", width: "15vw" }}
        loading="lazy"
      />
    </div>
  );
};

interface BannerSwapFormProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  toast: React.RefObject<Toast>;
  editMode?: boolean;
  existingSwap?: BannerSwap | null;
  /** Called after a new swap is queued successfully (e.g. refetch dashboard list). */
  onSwapQueued?: () => void;
  /** Pre-select channels when opening from Default banners (or elsewhere). */
  initialChannelIds?: string[];
  /** Pre-check "Set as new default" when opening from channel tools. */
  initialMakeDefault?: boolean;
  /** Override dialog title (e.g. Set new default flow). */
  dialogHeader?: string;
}

interface IFormInput {
  swapName: string;
  channelIDs: string[];
  startDate: Date;
  endDate: Date;
  description: string;
}

// Extended channel info for table display
interface ExtendedChannelInfo extends ChannelSettings {
  CurrentBannerCachedURL?: string;
}

function freshCreateFormDefaults(): IFormInput {
  return {
    swapName: "",
    channelIDs: [],
    startDate: new Date(),
    endDate: new Date(),
    description: "",
  };
}

const BannerSwapForm: React.FC<BannerSwapFormProps> = ({
  showDialog,
  setShowDialog,
  toast,
  editMode = false,
  existingSwap = null,
  onSwapQueued,
  initialChannelIds,
  initialMakeDefault = false,
  dialogHeader,
}) => {
  const [bannerSwapQueue, setBannerSwapQueue] = useState<BannerSwap[]>([]);
  const [channelDetails, setChannelDetails] = useState<ChannelSettingsWithId[]>(
    []
  );
  const [channelIdsListReady, setChannelIdsListReady] = useState(false);
  /** Always-current allowlist for yup (resolver schema can be stale across renders). */
  const channelIdsListRef = useRef<string[]>([]);

  const fetchBannerSwapQueue = async () => {
    const data = await apiService.bannerSwap.getBannerSwapQueue();
    const actionableBannerSwaps: BannerSwap[] = [];
    data.forEach((swap: BannerSwap) => {
      if (["running", "scheduled"].includes(swap.status)) {
        actionableBannerSwaps.push(swap);
      }
    });
    setBannerSwapQueue(actionableBannerSwaps);
  };

  useEffect(() => {
    if (!showDialog) {
      return;
    }
    void fetchBannerSwapQueue();
  }, [showDialog]);

  const schema = useMemo(
    () =>
      yup.object().shape({
    swapName: yup.string().trim().required("Swap name is required"),
    channelIDs: yup
      .array()
      .of(yup.string().required())
      .required("At least one channel ID is required")
      .min(1, "At least one channel ID is required")
      .test("is-valid-channel-id", "", function (value) {
        const { createError } = this;
        const allowlist = channelIdsListRef.current;
        if (!allowlist.length) {
          return true;
        }
        const invalidChannelIds = (value ?? []).filter(
          (val) => !allowlist.includes(val.trim())
        );
        if (invalidChannelIds.length > 0) {
          return createError({
            message: `Invalid channel ID(s): ${invalidChannelIds.join(", ")}`,
          });
        }
        return true;
      }),
    startDate: yup.date().required("Start date is required"),
    endDate: yup.date().required("End date is required"),
    description: yup.string().required("Description is required"),
      }),
    []
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    reset,
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    defaultValues:
      editMode && existingSwap
        ? {
            swapName: existingSwap.swapName,
            channelIDs: existingSwap.channelIDs,
            startDate:
              existingSwap.startDate instanceof Date
                ? existingSwap.startDate
                : new Date(existingSwap.startDate),
            endDate:
              existingSwap.endDate instanceof Date
                ? existingSwap.endDate
                : new Date(existingSwap.endDate),
            description: existingSwap.description,
          }
        : {
            swapName: "",
            channelIDs: [],
            startDate: new Date(),
            endDate: new Date(),
            description: "",
          },
  });

  const user = useSelector((state: any) => state.googleAuth.user);
  const watchedChannelIDs = watch("channelIDs");
  const watchedStartDate = watch("startDate");
  const watchedEndDate = watch("endDate");

  const selectedChannelRows = useMemo(
    () =>
      channelDetails.filter((channel) =>
        watchedChannelIDs?.includes(channel.ChannelId || channel.id)
      ),
    [channelDetails, watchedChannelIDs]
  );

  const [channelBannerURL, setChannelBannerUrl] = useState<string | null>(
    editMode && existingSwap ? existingSwap.channelBannerURL : null
  );
  const [channelBannerImage, setChannelBannerImage] = useState<string | null>(
    editMode && existingSwap ? existingSwap.channelBannerImage : null
  );
  const [isImmediate, setIsImmediate] = useState<boolean>(!editMode);
  const [makeDefault, setMakeDefault] = useState<boolean>(false);
  const [missingDefaultDialogOpen, setMissingDefaultDialogOpen] =
    useState(false);
  const [missingDefaultChannelList, setMissingDefaultChannelList] = useState<
    { channelId: string; channelTitle?: string }[]
  >([]);
  const [missingDefaultDialogReason, setMissingDefaultDialogReason] = useState<
    "missing" | "invalid"
  >("missing");
  const [overlapDialogOpen, setOverlapDialogOpen] = useState(false);
  const [overlapDialogData, setOverlapDialogData] =
    useState<BannerSwapOverlapAnalysis | null>(null);
  const [overlapSubmitting, setOverlapSubmitting] = useState(false);
  const pendingSwapRef = useRef<BannerSwapQueueRequest | null>(null);

  const effectiveSchedule = useMemo(() => {
    if (makeDefault) {
      return null;
    }
    const startDate = isImmediate ? new Date() : watchedStartDate;
    const endDate = watchedEndDate;
    return { startDate, endDate };
  }, [isImmediate, makeDefault, watchedStartDate, watchedEndDate]);

  const overlapAnalysis = useMemo(() => {
    if (!watchedChannelIDs?.length || editMode || makeDefault) {
      return null;
    }
    if (!effectiveSchedule) {
      return null;
    }
    return buildClientOverlapCheckResult({
      channelIDs: watchedChannelIDs,
      startDate: effectiveSchedule.startDate,
      endDate: effectiveSchedule.endDate,
      queue: bannerSwapQueue,
      excludeSwapUID: existingSwap?.swapUID,
    });
  }, [
    watchedChannelIDs,
    effectiveSchedule,
    bannerSwapQueue,
    editMode,
    makeDefault,
    existingSwap?.swapUID,
  ]);

  const changeDefaultPreview = useMemo(() => {
    if (!makeDefault || !watchedChannelIDs?.length) {
      return null;
    }
    const immediate: string[] = [];
    const deferred: string[] = [];
    for (const channelId of watchedChannelIDs) {
      const row = channelDetails.find(
        (c) => c.ChannelId === channelId || c.id === channelId
      );
      const state = row ? resolveBannerState(row) : "default";
      if (state === "brand_boost") {
        deferred.push(channelId);
      } else {
        immediate.push(channelId);
      }
    }
    return { immediate, deferred };
  }, [makeDefault, watchedChannelIDs, channelDetails]);

  const conflictChannelIdSet = useMemo(
    () => new Set(overlapAnalysis?.conflictChannelIds ?? []),
    [overlapAnalysis]
  );

  const defaultBannersPagePath = ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS;
  const fileUploadRef = useRef<FileUploadAreaRef>(null);

  const resetCreateForm = useCallback(() => {
    reset(freshCreateFormDefaults());
    setChannelBannerUrl(null);
    setChannelBannerImage(null);
    setIsImmediate(true);
    setMakeDefault(false);
    setChannelDetails([]);
    setMissingDefaultDialogOpen(false);
    setMissingDefaultChannelList([]);
    setMissingDefaultDialogReason("missing");
    setOverlapDialogOpen(false);
    setOverlapDialogData(null);
    pendingSwapRef.current = null;
    fileUploadRef.current?.clear();
  }, [reset]);

  /**
   * Queue 400 from the saved-default banner check: open the existing
   * not-upload-ready dialog instead of a toast that reads as an upload error.
   */
  const handleInvalidDefaultBannerError = useCallback(
    (error: unknown): boolean => {
      const rows = getInvalidDefaultBannerRows(error);
      if (!rows) {
        return false;
      }
      setOverlapDialogOpen(false);
      setOverlapDialogData(null);
      pendingSwapRef.current = null;
      setMissingDefaultChannelList(
        toMissingDefaultChannels(rows, channelDetails)
      );
      setMissingDefaultDialogReason("invalid");
      setMissingDefaultDialogOpen(true);
      return true;
    },
    [channelDetails]
  );

  useEffect(() => {
    if (isImmediate) {
      setValue("startDate", new Date(), { shouldValidate: true });
    }
  }, [isImmediate, setValue]);

  useEffect(() => {
    if (!showDialog || editMode || !initialChannelIds?.length) {
      return;
    }
    setValue("channelIDs", [...initialChannelIds], {
      shouldValidate: channelIdsListReady,
    });
  }, [
    showDialog,
    initialChannelIds,
    editMode,
    setValue,
    channelIdsListReady,
  ]);

  useEffect(() => {
    if (!showDialog || editMode) {
      return;
    }
    if (initialMakeDefault) {
      setMakeDefault(true);
      setIsImmediate(false);
    }
  }, [showDialog, editMode, initialMakeDefault]);

  useEffect(() => {
    if (showDialog || editMode) {
      return;
    }
    setMakeDefault(false);
  }, [showDialog, editMode]);

  useEffect(() => {
    let cancelled = false;
    setChannelIdsListReady(false);

    const loadAllowlist = async () => {
      try {
        const ids = await apiService.channelSettings.getChannelSettingsIds();
        if (cancelled) {
          return;
        }
        const normalized = Array.from(
          new Set(ids.map((id) => id.trim()).filter(Boolean))
        );
        channelIdsListRef.current = normalized;
        setChannelIdsListReady(true);
      } catch (error) {
        console.error("Error fetching channel IDs:", error);
        try {
          const settings =
            await apiService.channelSettings.getAllChannelSettings();
          if (cancelled) {
            return;
          }
          const fromSettings = Array.from(
            new Set(
              settings
                .flatMap((s) => [
                  (s.ChannelId || "").trim(),
                  (s.id || "").trim(),
                ])
                .filter(Boolean)
            )
          );
          channelIdsListRef.current = fromSettings;
          setChannelIdsListReady(true);
        } catch (fallbackErr) {
          console.error("Fallback channel allowlist failed:", fallbackErr);
          channelIdsListRef.current = [];
        }
      }
    };

    void loadAllowlist();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!channelIdsListReady) {
      return;
    }
    const current = watch("channelIDs");
    if (current?.length) {
      void trigger("channelIDs");
    }
  }, [channelIdsListReady, trigger, watch]);

  // Fetch channel details when channelIDs change
  useEffect(() => {
    const fetchChannelDetails = async () => {
      if (watchedChannelIDs && watchedChannelIDs.length > 0) {
        try {
          const details =
            await apiService.channelSettings.getAllChannelSettings();
          setChannelDetails(details);
        } catch (error) {
          console.error("Error fetching channel details:", error);
          setChannelDetails([]);
        }
      } else {
        setChannelDetails([]);
      }
    };

    fetchChannelDetails();
  }, [watchedChannelIDs]);

  const handleImageUrlChange = (newImageUrl: string | null) => {
    setChannelBannerUrl(newImageUrl);
  };

  const finalizeChangeDefault = async (
    channelIDs: string[],
    bannerURL: string,
    bannerImage: string
  ) => {
    const result = await bannerDashboardApi.postChangeDefaultBanner({
      channelIDs,
      channelBannerURL: bannerURL,
      channelBannerImage: bannerImage,
    });

    resetCreateForm();
    setShowDialog(false);
    onSwapQueued?.();
    void fetchBannerSwapQueue();

    const parts: string[] = [];
    if (result.appliedImmediately.length > 0) {
      parts.push(
        `Applied immediately on ${result.appliedImmediately.length} channel(s).`
      );
    }
    if (result.deferredUntilSwapEnd.length > 0) {
      parts.push(
        `${result.deferredUntilSwapEnd.length} channel(s) will switch when the current swap ends.`
      );
    }
    if (result.failed.length > 0) {
      toast?.current?.show({
        severity: "warn",
        summary: "Partial success",
        detail: `${parts.join(" ")} Failed: ${result.failed
          .map((f) => f.channelId)
          .join(", ")}.`,
        life: 5000,
      });
      return;
    }

    toast.current?.show({
      severity: "success",
      summary: "Default banner updated",
      detail: parts.join(" ") || result.message,
      life: 4000,
    });
  };

  const finalizeSwapQueue = async (
    swap: BannerSwapQueueRequest,
    _channelIdsForDefaultUpdate: string[]
  ) => {
    const response = await bannerDashboardApi.postBannerSwap(swap);

    setOverlapDialogOpen(false);
    setOverlapDialogData(null);
    pendingSwapRef.current = null;
    resetCreateForm();
    setShowDialog(false);
    onSwapQueued?.();
    void fetchBannerSwapQueue();
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: response?.message || "Banner swap created successfully",
    });
  };

  const handleOverlapResolutionConfirm = async (
    resolution: BannerSwapOverlapResolution
  ) => {
    const pending = pendingSwapRef.current;
    if (!pending) {
      return;
    }
    const channelIdsForDefault =
      resolution.strategy === "remove_conflicts"
        ? (overlapDialogData?.cleanChannelIds ?? pending.channelIDs)
        : pending.channelIDs;
    setOverlapSubmitting(true);
    try {
      await finalizeSwapQueue(
        { ...pending, overlapResolution: resolution },
        channelIdsForDefault
      );
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { message?: string; overlap?: BannerSwapOverlapAnalysis };
        };
      };
      if (err.response?.status === 409 && err.response.data?.overlap) {
        setOverlapDialogData(err.response.data.overlap);
        return;
      }
      if (handleInvalidDefaultBannerError(error)) {
        return;
      }
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "Failed to queue banner swap",
      });
    } finally {
      setOverlapSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<IFormInput> = async (values: IFormInput) => {
    let bannerURL = channelBannerURL;
    let bannerImage = channelBannerImage;

    // If not in edit mode and no banner URL yet, trigger upload first
    if (!editMode && !bannerURL) {
      if (!fileUploadRef.current?.hasFile()) {
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Please select a banner image",
        });
        return;
      }

      try {
        // Upload the banner before proceeding
        const uploadResult = await fileUploadRef.current.upload();
        bannerURL = uploadResult.url;
        bannerImage = uploadResult.imageName;
      } catch (error: any) {
        toast?.current?.show({
          severity: "error",
          summary: "Upload Failed",
          detail: error.message || "Failed to upload banner image",
        });
        return;
      }
    }

    if (!bannerURL || !bannerImage) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Banner image is required",
      });
      return;
    }

    if (makeDefault) {
      try {
        trackToolAction("BannerSwap", "set_default");
        await finalizeChangeDefault(values.channelIDs, bannerURL, bannerImage);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            err.response?.data?.message ||
            "Failed to change default banner",
        });
      }
      return;
    }

    const missingDefaults = channelsMissingDefaultBanner(
      values.channelIDs,
      channelDetails
    );
    if (missingDefaults.length > 0) {
        setMissingDefaultChannelList(
          missingDefaults.map((channel) => ({
            channelId: channel.ChannelId,
            channelTitle: channel.ChannelTitle,
          }))
        );
        setMissingDefaultDialogReason("missing");
        setMissingDefaultDialogOpen(true);
        return;
      }

    try {
      if (!user) {
        throw new Error("User not found");
      }

      trackToolAction("BannerSwap", "queue_swap");
      const swap: BannerSwapQueueRequest = {
        createdBy: user.email,
        swapName: values.swapName,
        channelIDs: values.channelIDs,
        startDate: isImmediate ? new Date() : values.startDate,
        endDate: values.endDate,
        description: values.description,
        channelBannerURL: bannerURL,
        channelBannerImage: bannerImage,
      };

      if (editMode && existingSwap) {
        toast?.current?.show({
          severity: "info",
          summary: "Update",
          detail: "Update functionality coming soon",
        });
        return;
      }

      if (overlapAnalysis?.hasConflicts) {
        pendingSwapRef.current = swap;
        setOverlapDialogData(overlapAnalysis);
        setOverlapDialogOpen(true);
        return;
      }

      await finalizeSwapQueue(swap, values.channelIDs);
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { message?: string; overlap?: BannerSwapOverlapAnalysis };
        };
      };
      if (err.response?.status === 409 && err.response.data?.overlap) {
        pendingSwapRef.current = {
          createdBy: user!.email,
          swapName: values.swapName,
          channelIDs: values.channelIDs,
          startDate: isImmediate ? new Date() : values.startDate,
          endDate: values.endDate,
          description: values.description,
          channelBannerURL: bannerURL!,
          channelBannerImage: bannerImage!,
        };
        setOverlapDialogData(err.response.data.overlap);
        setOverlapDialogOpen(true);
        return;
      }
      if (handleInvalidDefaultBannerError(error)) {
        return;
      }
      console.error(error);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || "An error occurred",
      });
    }
  };

  const handleCancel = () => {
    if (editMode && existingSwap) {
      // Cancel the swap
      apiService.bannerSwap.updateBannerSwapStatus(
        existingSwap.swapUID,
        "cancelled"
      );
      toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Swap cancelled successfully",
      });
      setShowDialog(false);
    } else {
      resetCreateForm();
      setShowDialog(false);
    }
  };

  const handleDialogHide = () => {
    if (!editMode) {
      resetCreateForm();
    }
    setShowDialog(false);
  };

  const handleReset = () => {
    resetCreateForm();
  };

  const minDate = new Date();

  const calendarInputClass = (hasError: boolean) =>
    hasError ? "border-red-500 border-2 w-full" : "w-full";

  // Channel table columns
  const channelNameColumn = (rowData: ExtendedChannelInfo) => {
    return (
      <span className="text-sm text-gray-900">
        {rowData.ChannelTitle || "N/A"}
      </span>
    );
  };

  const brandColumn = (rowData: ExtendedChannelInfo) => {
    return (
      <span className="text-sm text-gray-900">{rowData.Brand || "N/A"}</span>
    );
  };

  const languageColumn = (rowData: ExtendedChannelInfo) => {
    return (
      <span className="text-sm text-gray-900">{rowData.Language || "N/A"}</span>
    );
  };

  const channelTypeColumn = (rowData: ExtendedChannelInfo) => {
    return (
      <span className="text-sm text-gray-900">{rowData.Type || "N/A"}</span>
    );
  };

  const channelManagerColumn = (rowData: ExtendedChannelInfo) => {
    return (
      <span className="text-sm text-gray-900">
        {rowData.ChannelManager || "N/A"}
      </span>
    );
  };

  const currentBannerColumn = (rowData: ExtendedChannelInfo) => {
    const displayUrl = resolveCurrentBannerDisplayUrl(rowData);
    if (!displayUrl) {
      return <span className="text-sm text-gray-400">No banner</span>;
    }
    return (
      <div className="banner-swap-form__current-banner-cell">
        <img
          src={displayUrl}
          alt={`Current banner for ${rowData.ChannelTitle || rowData.ChannelId}`}
          className="banner-swap-form__current-banner-img"
          loading="lazy"
        />
      </div>
    );
  };

  const header = () =>
    dialogHeader ?? (makeDefault ? "Set as new default" : "New Swap");

  const footer = () => {
    return (
      <div className="flex justify-end gap-5" style={{ width: "100%" }}>
        {editMode ? (
          <>
            <Button
              label="Edit Swap"
              icon="pi pi-pencil"
              onClick={handleSubmit(onSubmit)}
              className="px-6 py-2.5 text-sm font-medium rounded-md"
              style={{
                backgroundColor: "#ff6b35",
                color: "#fff",
                border: "none",
              }}
            />
            <Button
              label="Cancel Swap"
              icon="pi pi-times"
              onClick={handleCancel}
              className="px-6 py-2.5 text-sm font-medium rounded-md"
              style={{
                backgroundColor: "#e24c4c",
                color: "#fff",
                border: "none",
              }}
            />
          </>
        ) : (
          <>
            <Button
              label="Reset"
              icon="pi pi-refresh"
              onClick={handleReset}
              className="px-6 py-2.5 text-sm font-medium rounded-md"
              style={{
                backgroundColor: "#e24c4c",
                color: "#fff",
                border: "none",
              }}
            />
            <Button
              type="submit"
              label={makeDefault ? "Set as new default" : "Confirm"}
              icon="pi pi-check"
              // disabled={!channelBannerURL}
              onClick={handleSubmit(onSubmit)}
              className="px-6 py-2.5 text-sm font-medium rounded-md"
              style={{
                backgroundColor: "#22c55e",
                color: "#fff",
                border: "none",
              }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog
      className="banner-swap-form-dialog"
      header={header}
      footer={footer}
      visible={showDialog}
      contentStyle={{ overflow: "auto", padding: "1rem 1.25rem" }}
      onHide={handleDialogHide}
      pt={{
        footer: {
          className: "p-2 text-sm flex justify-end",
          style: { width: "100%", justifyContent: "flex-end" },
        },
        header: {
          className: "p-3 text-sm font-semibold text-gray-900",
        },
      }}
      closable
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="banner-swap-form"
      >
        {/* Left Column - All Form Fields */}
        <div className="banner-swap-form__column">
          <div className="p-fluid w-full min-w-0">
            <div className="banner-swap-form__row-two">
              {/* Swap Name */}
              <div className="field m-0">
                <label
                  htmlFor="swapName"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  Swap Name
                </label>
                <Controller
                  name="swapName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      id="swapName"
                      placeholder="Enter a swap name"
                      className={`w-full text-sm text-gray-900 ${
                        errors.swapName
                          ? "border-red-500 border-2"
                          : "border-gray-300"
                      }`}
                    />
                  )}
                />
                {errors.swapName && (
                  <small className="text-red-500 text-xs mt-1 block">
                    {errors.swapName.message}
                  </small>
                )}
              </div>

              {/* Swap Type */}
              <div className="field m-0">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Swap Type
                </label>
                <div className="banner-swap-form__swap-type">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId="immediate"
                      checked={isImmediate}
                      disabled={makeDefault}
                      onChange={(event) => {
                        const checked = !!event.checked;
                        setIsImmediate(checked);
                        if (checked) {
                          setMakeDefault(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="immediate"
                      className={`m-0 text-sm text-gray-900 ${
                        makeDefault ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      Immediate
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId="makeDefault"
                      checked={makeDefault}
                      disabled={isImmediate}
                      onChange={(event) => {
                        const checked = !!event.checked;
                        setMakeDefault(checked);
                        if (checked) {
                          setIsImmediate(false);
                        }
                      }}
                    />
                    <label
                      htmlFor="makeDefault"
                      className={`m-0 text-sm text-gray-900 ${
                        isImmediate ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      Set as new default
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 m-0 mt-1">
                  Immediate and Set as new default cannot both be selected.
                </p>
              </div>
            </div>

            {makeDefault ? (
              <div className="banner-swap-form__change-default-callout mt-4">
                <p className="banner-swap-form__change-default-title">
                  This updates the channel default banner — not a scheduled swap.
                </p>
                {changeDefaultPreview ? (
                  <ul className="banner-swap-form__change-default-list">
                    {changeDefaultPreview.immediate.length > 0 ? (
                      <li>
                        <strong>Apply now</strong> (
                        {changeDefaultPreview.immediate.length} channel
                        {changeDefaultPreview.immediate.length === 1 ? "" : "s"}
                        ): banner uploads immediately because the channel is
                        showing its default.
                      </li>
                    ) : null}
                    {changeDefaultPreview.deferred.length > 0 ? (
                      <li>
                        <strong>Apply when swap ends</strong> (
                        {changeDefaultPreview.deferred.length} channel
                        {changeDefaultPreview.deferred.length === 1 ? "" : "s"}
                        ): default is updated now; the live banner switches when
                        the running swap reverts.
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            ) : (
            /* Start / End dates */
            <div className="banner-swap-form__row-two mt-4">
              <div className="field m-0">
                <label
                  htmlFor="startDate"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  Start Date
                </label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      value={isImmediate ? new Date() : field.value}
                      showTime
                      hourFormat="24"
                      showIcon
                      dateFormat="dd MMM yy"
                      minDate={minDate}
                      disabled={isImmediate}
                      className="w-full"
                      inputClassName={calendarInputClass(!!errors.startDate)}
                    />
                  )}
                />
                {errors.startDate && (
                  <small className="text-red-500 text-xs mt-1 block">
                    {errors.startDate.message}
                  </small>
                )}
              </div>

              <div className="field m-0">
                <label
                  htmlFor="endDate"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  End Date
                </label>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      value={makeDefault ? new Date("9999-12-31") : field.value}
                      showTime
                      hourFormat="24"
                      showIcon
                      dateFormat="dd MMM yy"
                      minDate={watch("startDate") || minDate}
                      disabled={makeDefault}
                      className="w-full"
                      inputClassName={calendarInputClass(!!errors.endDate)}
                    />
                  )}
                />

                {errors.endDate && (
                  <small className="text-red-500 text-xs mt-1 block">
                    {errors.endDate.message}
                  </small>
                )}
              </div>
            </div>
            )}

            {/* Channel IDs */}
            {overlapAnalysis?.hasConflicts ? (
              <div
                className="banner-swap-form__overlap-alert mt-4"
                role="alert"
              >
                <p className="banner-swap-form__overlap-alert-title">
                  {overlapSummaryText(overlapAnalysis)}
                </p>
                <ul className="banner-swap-form__overlap-alert-list">
                  {overlapAnalysis.conflicts.map((conflict) => (
                    <li key={conflict.channelId}>
                      <span className="font-mono">{conflict.channelId}</span>
                      {conflict.conflictingSwaps.map((swap) => (
                        <span key={`${conflict.channelId}-${swap.swapUID}`}>
                          {" "}
                          · #{swap.swapUID} {swap.swapName} (
                          {formatSwapDateCell(swap.startDate)} –{" "}
                          {formatSwapDateCell(swap.endDate)})
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="field w-full min-w-0 mt-4">
              <label
                htmlFor="channelIDs"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Channel IDs
              </label>
              <Controller
                name="channelIDs"
                control={control}
                render={({ field }) => (
                  <Chips
                    {...field}
                    onChange={(values) =>
                      field.onChange(
                        values.value
                          ?.map((value) => value.trim())
                          .filter(Boolean)
                      )
                    }
                    separator="\n"
                    allowDuplicate={false}
                    placeholder="Enter channel IDs (one per line or comma separated)"
                    className={`w-full text-sm text-gray-900 banner-swap-form__chips ${
                      errors.channelIDs
                        ? "border-red-500 border-2"
                        : "border-gray-300"
                    }`}
                    itemTemplate={(item: string) => (
                      <span
                        className={
                          conflictChannelIdSet.has(item)
                            ? "banner-swap-form__chip--overlap"
                            : undefined
                        }
                      >
                        {item}
                      </span>
                    )}
                  />
                )}
              />
              {errors.channelIDs && (
                <small className="text-red-500 text-xs mt-1 block">
                  {errors.channelIDs.message}
                </small>
              )}
            </div>

            {/* Channel List Table */}
            <div className="banner-swap-form__table-wrap mt-4">
              <DataTable
                value={selectedChannelRows}
                scrollable={selectedChannelRows.length > 2}
                scrollHeight={selectedChannelRows.length > 2 ? "280px" : undefined}
                showGridlines={false}
                className="banner-swap-form__channel-table text-sm"
                size="small"
                rowClassName={(row: ExtendedChannelInfo) => {
                  const channelId = row.ChannelId || "";
                  return conflictChannelIdSet.has(channelId)
                    ? "banner-swap-form__row--overlap"
                    : "";
                }}
                emptyMessage={
                  watchedChannelIDs && watchedChannelIDs.length > 0
                    ? "Loading channel details..."
                    : "No channels selected. Add channel IDs above to see details."
                }
              >
                <Column
                  header="Channel Name"
                  body={channelNameColumn}
                  sortable
                  headerClassName="text-sm font-medium text-gray-700"
                />
                <Column
                  header="Current Banner"
                  body={currentBannerColumn}
                  bodyClassName="banner-swap-form__banner-td"
                  headerClassName="text-sm font-medium text-gray-700"
                  style={{ width: "9rem" }}
                />
                <Column
                  header="Brand"
                  body={brandColumn}
                  sortable
                  headerClassName="text-sm font-medium text-gray-700"
                />
                <Column
                  header="Language"
                  body={languageColumn}
                  sortable
                  headerClassName="text-sm font-medium text-gray-700"
                />
                <Column
                  header="Channel Type"
                  body={channelTypeColumn}
                  sortable
                  headerClassName="text-sm font-medium text-gray-700"
                />
                <Column
                  header="Channel Manager"
                  body={channelManagerColumn}
                  sortable
                  headerClassName="text-sm font-medium text-gray-700"
                />
              </DataTable>
            </div>
          </div>
        </div>

        {/* Right Column - Banner Content Only */}
        <div className="banner-swap-form__column">
          {!editMode && (
            <div className="banner-swap-form__section w-full">
              <label
                htmlFor="bannerUpload"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Banner Upload
              </label>
              <FileUploadArea
                ref={fileUploadRef}
                onImageUrlChange={handleImageUrlChange}
                onImageNameChange={setChannelBannerImage}
              />
            </div>
          )}

          <div className="banner-swap-form__section w-full">
            <label
              htmlFor="description"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <InputTextarea
                  {...field}
                  title="Description"
                  id="description"
                  rows={6}
                  className={`w-full text-sm text-gray-900 ${
                    errors.description
                      ? "border-red-500 border-2"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter description for this banner swap..."
                />
              )}
            />
            {errors.description && (
              <small className="text-red-500 text-xs mt-1 block">
                {errors.description.message}
              </small>
            )}
          </div>

          {/* Previous Banner Preview */}
          {editMode && channelDetails.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-3 text-gray-900">
                Previous Banner Preview
              </h3>
              <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 relative">
                {channelDetails[0] && (
                  <BannerTemplateDefaultWrapper
                    channelInfo={channelDetails[0]}
                  />
                )}
                <div className="absolute top-3 right-3 flex gap-2">
                  <Button
                    icon="pi pi-eye"
                    rounded
                    text
                    className="text-gray-600"
                    tooltip="View"
                  />
                  <Button
                    icon="pi pi-download"
                    rounded
                    text
                    className="text-gray-600"
                    tooltip="Download"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
      <MissingDefaultBannerDialog
        visible={missingDefaultDialogOpen}
        onHide={() => {
          setMissingDefaultDialogOpen(false);
          setMissingDefaultChannelList([]);
          setMissingDefaultDialogReason("missing");
        }}
        channels={missingDefaultChannelList}
        defaultBannersPath={defaultBannersPagePath}
        reason={missingDefaultDialogReason}
      />
      {overlapDialogData ? (
        <SwapOverlapResolutionDialog
          visible={overlapDialogOpen}
          onHide={() => {
            if (overlapSubmitting) {
              return;
            }
            setOverlapDialogOpen(false);
            setOverlapDialogData(null);
            pendingSwapRef.current = null;
          }}
          overlap={overlapDialogData}
          overlapCheckParams={
            pendingSwapRef.current
              ? {
                  channelIDs: pendingSwapRef.current.channelIDs,
                  startDate: pendingSwapRef.current.startDate,
                  endDate: pendingSwapRef.current.endDate,
                }
              : undefined
          }
          onConfirm={handleOverlapResolutionConfirm}
          submitting={overlapSubmitting}
        />
      ) : null}
    </Dialog>
  );
};

export default BannerSwapForm;
